import { createContext, useContext, useEffect, useState } from "react"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

const initialState: ThemeProviderState = {
  theme: "light",
  setTheme: () => null,
  toggleTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "permut-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme,
  )

  useEffect(() => {
    const root = window.document.documentElement
    const systemQuery = window.matchMedia("(prefers-color-scheme: dark)")

    function applyTheme() {
      const isDark = theme === "dark" || (theme === "system" && systemQuery.matches)

      root.classList.remove("light", "dark")
      root.classList.add(isDark ? "dark" : "light")
    }

    applyTheme()

    if (theme !== "system") return

    systemQuery.addEventListener("change", applyTheme)

    return () => systemQuery.removeEventListener("change", applyTheme)
  }, [theme])

  function setThemeContext(theme: Theme) {
    localStorage.setItem(storageKey, theme)
    setTheme(theme)
  }

  function toggleTheme() {
    const newTheme = theme === "dark" ? "light" : "dark"
    setThemeContext(newTheme)
  }

  const value = {
    theme,
    setTheme: setThemeContext,
    toggleTheme,
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined) throw new Error("useTheme must be used within a ThemeProvider")

  return context
}
